import { normalizePortalPath } from '../../../../utilities/normalizePortalPath';
import React, { ChangeEvent } from 'react';
import { createFileRoute, useNavigate, useRouterState } from '@tanstack/react-router'
import type { FileRouteTypes } from '@tanstack/react-router';
import { useForm } from '@tanstack/react-form';
import { useAuth } from '../../../../contexts/AuthContext';
import { Unauthorized } from '../../../../components/Unauthorized';
import { Chart, PatientDoc, Product, IsSigningObject, FileTypes } from '@usacsmideo/types';
import { abnSignaturePath } from '../../../../data/payment/abnSignaturePath';
import useChart from '../../../../hooks/useChart';
import usePatientDoc from '../../../../hooks/usePatientDoc';
import useProducts from '../../../../hooks/useProducts';
import { FormInput } from '../../../../components/forms/FormInput';
import { Button } from '../../../../components/buttons/Button';
import { useUpdateChartSection } from '../../../../hooks/useUpdateChartSection';
import { useInitChartSection } from '../../../../hooks/useInitChartSection';
import { notifyChartSaveError } from '../../../../utilities/chartSaveError';
import { SignatureArea } from '../../../../components/SignatureArea';
import { Heading } from '../../../../components/Heading';
import * as Sentry from '@sentry/browser';
import Checkbox from '../../../../components/forms/Checkbox';
import { classNames } from '../../../../style/classNames';
import { updateNavObjAndGetNextScreen } from '../../../../data/navigation/updateNavObjAndGetNextScreen';
import { useUpdateUserDoc } from '../../../../hooks/useUpdateUserDoc';
export const Route = createFileRoute('/_authenticated/$patientId/payment/insuranceABN')({
  component: InsuranceABNPage,
});

function InsuranceABNPage() {
  const { patientId } = Route.useParams();
  const { currentUser } = useAuth();
  const chartQuery = useChart(patientId);
  const chart = chartQuery.data as Chart;
  const productsDocQuery = useProducts();
  const patientDocQuery = usePatientDoc(patientId);
  const patientInfo = patientDocQuery.data as PatientDoc;
  const productsDocData = productsDocQuery.data as Product[];
  const { mutate, mutateAsync } = useUpdateChartSection();

  const [chosenOption, setChosenOption] = React.useState('');
  const [selectedProduct, setSelectedProduct] = React.useState<Product>();
  const [isSubmitted, setIsSubmitted] = React.useState(false);
  const [isSigning, setIsSigning] = React.useState({
    patientFinal: false,
    witness1: false,
    witness2: false,
    hipaa: false,
    consent: false,
    forms: false,
    abnMedicare: false,
    abn: false,
    aob1: false,
    aob2: false,
  } as IsSigningObject);
  const { mutate: mutatePatientDoc } = useUpdateUserDoc();
  const navigate = useNavigate();
  const routerState = useRouterState();
  const currentLocation = routerState.location;
  const pathname = currentLocation.pathname;

  const handleNextButtonClick = async () => {
    const res = await updateNavObjAndGetNextScreen({
      patientId,
      currentPage: pathname,
      patientInfo: patientInfo,
      chart: chart,
    });

    if ('incompleteMessage' in res) {
      // THE PATIENT HAS NOT COMPLETED THE NECESSARY SCREENS
      alert(`Incomplete Information: ${res.incompleteMessage}`);
      return;
    }
    if ('errorMessage' in res) {
      // FIRESTORE ERROR
      alert(`Error: ${res.errorMessage}`);
      return;
    }
    // NAVIGATION COMPLETE — PERSIST FINAL STATE AND NAVIGATE HOME
    if ('complete' in res) {
      mutatePatientDoc(
        { patientId: patientId, navObj: res.updatedNavObj },
        {
          onSuccess: () => {
            navigate({ to: '/' });
          },
          onError: (error: Error) => {
            Sentry.captureException(
              new Error(
                `Error updating patient navigation object for patient ${patientId}: ${error.message}`,
              ),
            );
            console.error('Error updating patient navigation object:', error);
            alert(
              'There was an error updating your information. Please try again later or contact support if this issue persists.',
            );
          },
        },
      );
      return;
    }


    mutatePatientDoc(
      {
        patientId: patientId,
        navObj: res.updatedNavObj,
      },
      {
        onSuccess: () => {
          // NAVIGATE TO THE NEXT SCREEN
          navigate({ to: normalizePortalPath(res.nextScreen.portalPath) as FileRouteTypes['to'] });
        },
        onError: (error: Error) => {
          Sentry.captureException(
            new Error(
              `Error updating patient navigation object for patient ${patientId}: ${error.message}`,
            ),
          );
          console.error('Error updating patient navigation object:', error);
          alert(
            'There was an error updating your information. Please try again later or contact support if this issue persists.',
          );
        },
      },
    );
  };

  // SET SELECTED PRODUCT
  React.useEffect(() => {
    if (productsDocData && chart) {
      let productFullName = '';
      switch (chart?.PRODUCT_INFO?.selectedProduct) {
        case 'essential':
          productFullName = 'Essential Package';
          break;
        case 'vital':
          productFullName = 'Vital Package';
          break;
        case 'vip':
          productFullName = 'Physician Guided Package';
          break;
        default:
          break;
      }
      setSelectedProduct(productsDocData.find((product) => product.name === productFullName));
    }
  }, [productsDocData, chart]);

  const medicareOptions = {
    optionOne:
      'OPTION 1. I will be asked to pay now, but I also want my insurance billed for an official decision on payment. I understand if my insurance doesn’t pay, I am responsible for payment, but I can appeal to my insurance carrier. If my insurance does pay, you will refund any payments I made to you, less co-pays or deductibles.',
    optionTwo:
      'OPTION 2. I want the MIDEO listed above, but do not bill my insurance. I understand that payment is required in advance of my MIDEO appointment. I also understand that I cannot appeal or make an insurance reimbursement claim if my insurance is not billed.',
  };

  // SET STATE TO TRIGGER EFFECT
  const handleCheckboxClick = (e: ChangeEvent<HTMLInputElement>) => {
    switch (e.target.name) {
      case 'optionOne':
        setChosenOption(medicareOptions.optionOne);
        break;
      case 'optionTwo':
        setChosenOption(medicareOptions.optionTwo);
        break;
      default:
        break;
    }
  };

  // THE FORM DATA TO WRITE — SHARED BY THE SUBMIT AND THE BACKGROUND INIT, SO BOTH WRITE THE SAME
  const abnInfoPayload = () => ({
    patientId: patientId,
    docData: {
      ABN_INFO: {
        additionalInfo: insuranceABNForm.state.values.additionalInfo,
        medicareABNChoice: chart?.ABN_INFO?.medicareABNChoice ? chart.ABN_INFO.medicareABNChoice : '',
        nonMedicareABNChoice: chosenOption,
        returning: true,
        signature1DataURI: chart?.ABN_INFO?.signature1DataURI ? chart.ABN_INFO.signature1DataURI : '',
      },
    },
  });

  // WRITE FORM DATA TO FIRESTORE
  const handleMedicareABNInfoUpdate = () => {
    mutate(abnInfoPayload(), {
      onSuccess: () => {
        // alert('Successfully updated document');
        setIsSubmitted(true);
      },
      onError: () => {
        notifyChartSaveError();
      },
    });
  };

  // INITIALIZE EMPTY FIRESTORE DOC IF IT DOESN'T EXIST — ONCE PER FAILURE, NOT ONCE PER SNAPSHOT
  useInitChartSection({
    needed: !!chart && !chart.ABN_INFO,
    write: () => mutateAsync(abnInfoPayload()).then(() => setIsSubmitted(true)),
  });

  React.useEffect(() => {
    if (chart?.ABN_INFO?.nonMedicareABNChoice) {
      setChosenOption(chart.ABN_INFO.nonMedicareABNChoice);
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chart]);

  // INITIALIZE FORM
  const insuranceABNForm = useForm({
    defaultValues: {
      additionalInfo: chart?.ABN_INFO?.additionalInfo ? chart.ABN_INFO.additionalInfo : '',
      medicareABNChoice: chart?.ABN_INFO?.medicareABNChoice ? chart.ABN_INFO.medicareABNChoice : '',
      nonMedicareABNChoice: chart?.ABN_INFO?.nonMedicareABNChoice ? chart.ABN_INFO.nonMedicareABNChoice : '',
      returning: chart?.ABN_INFO?.returning ? chart.ABN_INFO.returning : false,
      signature1DataURI: chart?.ABN_INFO?.signature1DataURI ? chart.ABN_INFO.signature1DataURI : '',
    },
    onSubmit: async () => {
      await handleMedicareABNInfoUpdate();
    },
  });

  if (chartQuery.isLoading || !chart) {
    return <div>Loading patient information...</div>;
  }
  if (!currentUser) {
    return <div>Loading current user</div>;
  }
  if (currentUser?.uid !== patientId) {
    return <Unauthorized />;
  }
  if (currentUser?.uid === patientId && chart?.PATIENT_INFO_1) {
    return (
      <>
        <div id="medicare-abn-page" className="flex flex-col items-center justify-center">
          <Heading id="medicare-abn-page-header" textContent="Advance Beneficiary Notice of Non-Coverage (ABN)" />
          <div id="medicare-abn-page-main" className="flex w-5/6 flex-row justify-around">
            <div className="flex flex-col items-start">
              <p className="text-xl">NOTE: If your insurance does not pay for MIDEO, you may have to pay.</p>
              <p id="abn-description-text" className="my-2 text-xl">
                Your Health Insurance does not pay for everything, even some care that you or your health care provider
                have good reason to believe you need. We expect your insurance may not pay for MIDEO. The reason your
                insurance may not pay for MIDEO is that it may be considered experimental. The cost of your visit and
                evaluation is ${selectedProduct?.oneTimePrice}.
              </p>
              <div id="option-checkbox-group" className="flex flex-col">
                <Checkbox
                  id="option-one"
                  label={medicareOptions.optionOne}
                  name="optionOne"
                  checked={chosenOption === medicareOptions.optionOne}
                  onChange={(e) => {
                    handleCheckboxClick(e);
                  }}
                />
                <Checkbox
                  id="option-two"
                  label={medicareOptions.optionTwo}
                  name="optionTwo"
                  checked={chosenOption === medicareOptions.optionTwo}
                  onChange={(e) => {
                    handleCheckboxClick(e);
                  }}
                />
              </div>
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  insuranceABNForm.handleSubmit();
                }}
              >
                <div className="my-4 flex flex-row">
                  <insuranceABNForm.Field
                    name="additionalInfo"
                    children={(field) => (
                      <>
                        <FormInput
                          label="Additional Info: "
                          id="additional-info"
                          name={field.name}
                          value={field.state.value}
                          handleBlur={field.handleBlur}
                          handleChange={(e) => field.handleChange(e.target.value)}
                          required={false}
                        />
                      </>
                    )}
                  />
                </div>
              </form>
              <SignatureArea
                elementId={'insurance-abn-sig'}
                type={FileTypes.insuranceABNSig}
                data={chart}
                patientId={patientId}
                isSigning={isSigning}
                cloudStoragePath={abnSignaturePath(patientId)}
                setIsSigning={setIsSigning}
                docToUpdate={'ABN_INFO'}
                sigName="abn"
                canComeFromClone={true}
              />
              <div className="flex w-full flex-col items-center">
                <Button
                  id="submit-insurance-abn-info"
                  type="submit"
                  textContent="Submit"
                  onClick={handleMedicareABNInfoUpdate}
                />
              </div>
            </div>
          </div>
          <div id="nav" className="flex flex-row">
            {isSubmitted && (
              <div className="flex flex-col items-center">
                <button
                  id="submit-form-info"
                  title="Submit Form Info"
                  onClick={handleNextButtonClick}
                  disabled={false}
                  className={classNames.nextButton}
                >
                  Next
                </button>
              </div>
            )}
          </div>
        </div>
      </>
    );
  }

  return <div>Loading...</div>;
}
